/**
 * RPC channel names — organized by domain namespace.
 * Wire-format strings (values) are the stable API contract.
 * Key paths are internal and may be reorganized freely.
 */
export const RPC_CHANNELS = {
  remote: {
    TEST_CONNECTION: 'remote:testConnection',
  },
  server: {
    GET_WORKSPACES: 'server:getWorkspaces',
    CREATE_WORKSPACE: 'server:createWorkspace',
    GET_STATUS: 'server:getStatus',
    GET_HEALTH: 'server:getHealth',
    GET_ACTIVE_SESSIONS: 'server:getActiveSessions',
    SHUTTING_DOWN: 'server:shuttingDown',
    STATUS_CHANGED: 'server:statusChanged',
    HOME_DIR: 'server:homeDir',
  },
  sessions: {
    GET: 'sessions:get',
    GET_UNREAD_SUMMARY: 'sessions:getUnreadSummary',
    MARK_ALL_READ: 'sessions:markAllRead',
    UNREAD_SUMMARY_CHANGED: 'sessions:unreadSummaryChanged',
    CREATE: 'sessions:create',
    DELETE: 'sessions:delete',
    GET_MESSAGES: 'sessions:getMessages',
    SEND_MESSAGE: 'sessions:sendMessage',
    CANCEL: 'sessions:cancel',
    KILL_SHELL: 'sessions:killShell',
    RESPOND_TO_PERMISSION: 'sessions:respondToPermission',
    RESPOND_TO_CREDENTIAL: 'sessions:respondToCredential',
    COMMAND: 'sessions:command',
    GET_PENDING_PLAN_EXECUTION: 'sessions:getPendingPlanExecution',
    GET_PERMISSION_MODE_STATE: 'sessions:getPermissionModeState',
    EVENT: 'session:event',
    GET_MODEL: 'session:getModel',
    SET_MODEL: 'session:setModel',
    GET_FILES: 'sessions:getFiles',
    GET_FOLDER_PATH: 'sessions:getFolderPath',
    GET_NOTES: 'sessions:getNotes',
    SET_NOTES: 'sessions:setNotes',
    WATCH_FILES: 'sessions:watchFiles',
    UNWATCH_FILES: 'sessions:unwatchFiles',
    FILES_CHANGED: 'sessions:filesChanged',
    SEARCH_CONTENT: 'sessions:searchContent',
    EXPORT: 'sessions:export',
    IMPORT: 'sessions:import',
    EXPORT_REMOTE_TRANSFER: 'sessions:exportRemoteTransfer',
    IMPORT_REMOTE_TRANSFER: 'sessions:importRemoteTransfer',
  },
  transfer: {
    START: 'transfer:start',
    CHUNK: 'transfer:chunk',
    COMMIT: 'transfer:commit',
    ABORT: 'transfer:abort',
  },
  tasks: {
    GET_OUTPUT: 'tasks:getOutput',
  },
  workspaces: {
    GET: 'workspaces:get',
    CREATE: 'workspaces:create',
    CHECK_SLUG: 'workspaces:checkSlug',
    UPDATE_REMOTE: 'workspaces:updateRemote',
  },
  window: {
    GET_WORKSPACE: 'window:getWorkspace',
    GET_MODE: 'window:getMode',
    OPEN_WORKSPACE: 'window:openWorkspace',
    OPEN_SESSION_IN_NEW_WINDOW: 'window:openSessionInNewWindow',
    SWITCH_WORKSPACE: 'window:switchWorkspace',
    CLOSE: 'window:close',
    CLOSE_REQUESTED: 'window:closeRequested',
    CONFIRM_CLOSE: 'window:confirmClose',
    CANCEL_CLOSE: 'window:cancelClose',
    SET_TRAFFIC_LIGHTS: 'window:setTrafficLights',
    FOCUS_STATE: 'window:focusState',
    GET_FOCUS_STATE: 'window:getFocusState',
  },
  file: {
    READ: 'file:read',
    READ_DATA_URL: 'file:readDataUrl',
    READ_PREVIEW_DATA_URL: 'file:readPreviewDataUrl',
    READ_BINARY: 'file:readBinary',
    OPEN_DIALOG: 'file:openDialog',
    READ_ATTACHMENT: 'file:readAttachment',
    READ_USER_ATTACHMENT: 'file:readUserAttachment',
    STORE_ATTACHMENT: 'file:storeAttachment',
    GENERATE_THUMBNAIL: 'file:generateThumbnail',
    LIST_SERVER_FILES: 'file:listServerFiles',
    PREPARE_DOWNLOAD: 'file:prepareDownload',
    READ_DOWNLOAD_CHUNK: 'file:readDownloadChunk',
    FINISH_DOWNLOAD: 'file:finishDownload',
  },
  fs: {
    SEARCH: 'fs:search',
    LIST_DIRECTORY: 'fs:listDirectory',
  },
  debug: {
    LOG: 'debug:log',
  },
  theme: {
    GET_SYSTEM_PREFERENCE: 'theme:getSystemPreference',
    SYSTEM_CHANGED: 'theme:systemChanged',
    APP_CHANGED: 'theme:appChanged',
    GET_APP: 'theme:getApp',
    GET_PRESETS: 'theme:getPresets',
    LOAD_PRESET: 'theme:loadPreset',
    GET_COLOR_THEME: 'theme:getColorTheme',
    SET_COLOR_THEME: 'theme:setColorTheme',
    BROADCAST_PREFERENCES: 'theme:broadcastPreferences',
    PREFERENCES_CHANGED: 'theme:preferencesChanged',
    GET_WORKSPACE_COLOR_THEME: 'theme:getWorkspaceColorTheme',
    SET_WORKSPACE_COLOR_THEME: 'theme:setWorkspaceColorTheme',
    GET_ALL_WORKSPACE_THEMES: 'theme:getAllWorkspaceThemes',
    BROADCAST_WORKSPACE_THEME: 'theme:broadcastWorkspaceTheme',
    WORKSPACE_THEME_CHANGED: 'theme:workspaceThemeChanged',
  },
  system: {
    VERSIONS: 'system:versions',
    HOME_DIR: 'system:homeDir',
    IS_DEBUG_MODE: 'system:isDebugMode',
    GET_WINDOW_BACKGROUND_MATERIAL: 'system:getWindowBackgroundMaterial',
  },
  update: {
    CHECK: 'update:check',
    DOWNLOAD: 'update:download',
    GET_INFO: 'update:getInfo',
    INSTALL: 'update:install',
    DISMISS: 'update:dismiss',
    GET_DISMISSED: 'update:getDismissed',
    AVAILABLE: 'update:available',
    DOWNLOAD_PROGRESS: 'update:downloadProgress',
  },
  shell: {
    OPEN_URL: 'shell:openUrl',
    OPEN_FILE: 'shell:openFile',
    SHOW_IN_FOLDER: 'shell:showInFolder',
  },
  menu: {
    NEW_CHAT: 'menu:newChat',
    NEW_WINDOW: 'menu:newWindow',
    OPEN_SETTINGS: 'menu:openSettings',
    KEYBOARD_SHORTCUTS: 'menu:keyboardShortcuts',
    TOGGLE_FOCUS_MODE: 'menu:toggleFocusMode',
    TOGGLE_SIDEBAR: 'menu:toggleSidebar',
    QUIT: 'menu:quit',
    MINIMIZE: 'menu:minimize',
    MAXIMIZE: 'menu:maximize',
    ZOOM_IN: 'menu:zoomIn',
    ZOOM_OUT: 'menu:zoomOut',
    ZOOM_RESET: 'menu:zoomReset',
    TOGGLE_DEV_TOOLS: 'menu:toggleDevTools',
    UNDO: 'menu:undo',
    REDO: 'menu:redo',
    CUT: 'menu:cut',
    COPY: 'menu:copy',
    PASTE: 'menu:paste',
    SELECT_ALL: 'menu:selectAll',
  },
  deeplink: {
    NAVIGATE: 'deeplink:navigate',
  },
  auth: {
    LOGOUT: 'auth:logout',
    SHOW_LOGOUT_CONFIRMATION: 'auth:showLogoutConfirmation',
    SHOW_DELETE_SESSION_CONFIRMATION: 'auth:showDeleteSessionConfirmation',
  },
  credentials: {
    HEALTH_CHECK: 'credentials:healthCheck',
  },
  onboarding: {
    GET_AUTH_STATE: 'onboarding:getAuthState',
    VALIDATE_MCP: 'onboarding:validateMcp',
    START_MCP_OAUTH: 'onboarding:startMcpOAuth',
    DEFER_SETUP: 'onboarding:deferSetup',
  },
  llmConnections: {
    LIST: 'LLM_Connection:list',
    LIST_WITH_STATUS: 'LLM_Connection:listWithStatus',
    GET: 'LLM_Connection:get',
    GET_API_KEY: 'LLM_Connection:getApiKey',
    SAVE: 'LLM_Connection:save',
    DELETE: 'LLM_Connection:delete',
    TEST: 'LLM_Connection:test',
    SET_DEFAULT: 'LLM_Connection:setDefault',
    SET_WORKSPACE_DEFAULT: 'LLM_Connection:setWorkspaceDefault',
    REFRESH_MODELS: 'LLM_Connection:refreshModels',
    CHANGED: 'LLM_Connection:changed',
  },
  settings: {
    GET_DEFAULT_THINKING_LEVEL: 'settings:getDefaultThinkingLevel',
    SET_DEFAULT_THINKING_LEVEL: 'settings:setDefaultThinkingLevel',
    GET_NETWORK_PROXY: 'settings:getNetworkProxy',
    SET_NETWORK_PROXY: 'settings:setNetworkProxy',
    GET_SERVER_CONFIG: 'settings:getServerConfig',
    SET_SERVER_CONFIG: 'settings:setServerConfig',
    GET_SERVER_STATUS: 'settings:getServerStatus',
    RESET_SERVER_ACCESS_TOKEN: 'settings:resetServerAccessToken',
    DISCONNECT_PUBLIC_ACCESS_CONNECTION: 'settings:disconnectPublicAccessConnection',
    DELETE_PUBLIC_ACCESS_DEVICE: 'settings:deletePublicAccessDevice',
    GET_WEBDAV_SYNC_SETTINGS: 'settings:getWebDavSyncSettings',
    SAVE_WEBDAV_SYNC_SETTINGS: 'settings:saveWebDavSyncSettings',
    TEST_WEBDAV_SYNC_CONNECTION: 'settings:testWebDavSyncConnection',
    RUN_WEBDAV_BACKUP: 'settings:runWebDavBackup',
    GET_WEBDAV_LATEST_SNAPSHOT: 'settings:getWebDavLatestSnapshot',
    RUN_WEBDAV_RESTORE: 'settings:runWebDavRestore',
    WEBDAV_SYNC_STATUS_CHANGED: 'settings:webDavSyncStatusChanged',
  },
  context: {
    GET_STATE: 'context:getState',
    SAVE_CONFIG: 'context:saveConfig',
    SAVE_MEMORY: 'context:saveMemory',
    CLEAR_MEMORY: 'context:clearMemory',
    REFINE_MEMORY: 'context:refineMemory',
    CREATE_DOCSET: 'context:createDocset',
    UPDATE_DOCSET: 'context:updateDocset',
    DELETE_DOCSET: 'context:deleteDocset',
    ADD_FILES: 'context:addFiles',
    ADD_URL: 'context:addUrl',
    REBUILD_INDEX: 'context:rebuildIndex',
    SEARCH_DOCSETS: 'context:searchDocsets',
  },
  dialog: {
    OPEN_FOLDER: 'dialog:openFolder',
  },
  preferences: {
    READ: 'preferences:read',
    WRITE: 'preferences:write',
  },
  drafts: {
    GET: 'drafts:get',
    SET: 'drafts:set',
    DELETE: 'drafts:delete',
    GET_ALL: 'drafts:getAll',
  },
  sources: {
    GET: 'sources:get',
    CREATE: 'sources:create',
    DELETE: 'sources:delete',
    START_OAUTH: 'sources:startOAuth',
    SAVE_CREDENTIALS: 'sources:saveCredentials',
    CHANGED: 'sources:changed',
    GET_PERMISSIONS: 'sources:getPermissions',
    GET_MCP_TOOLS: 'sources:getMcpTools',
  },
  oauth: {
    START: 'oauth:start',
    COMPLETE: 'oauth:complete',
    CANCEL: 'oauth:cancel',
    REVOKE: 'oauth:revoke',
  },
  workspace: {
    GET_PERMISSIONS: 'workspace:getPermissions',
    READ_IMAGE: 'workspace:readImage',
    WRITE_IMAGE: 'workspace:writeImage',
    SETTINGS_GET: 'workspaceSettings:get',
    SETTINGS_UPDATE: 'workspaceSettings:update',
  },
  permissions: {
    GET_DEFAULTS: 'permissions:getDefaults',
    DEFAULTS_CHANGED: 'permissions:defaultsChanged',
  },
  skills: {
    GET: 'skills:get',
    GET_FILES: 'skills:getFiles',
    MARKET_CATEGORIES: 'skills:marketCategories',
    MARKET_SEARCH: 'skills:marketSearch',
    MARKET_LIST: 'skills:marketList',
    MARKET_INSTALL: 'skills:marketInstall',
    TEAM_API: 'skills:teamApi',
    TEAM_PACKAGE_SKILL: 'skills:teamPackageSkill',
    TEAM_INSTALL_PACKAGE: 'skills:teamInstallPackage',
    DELETE: 'skills:delete',
    IMPORT: 'skills:import',
    IMPORT_FROM_PATH: 'skills:importFromPath',
    CLONE_TO_WORKSPACE: 'skills:cloneToWorkspace',
    OPEN_EDITOR: 'skills:openEditor',
    OPEN_FINDER: 'skills:openFinder',
    CHANGED: 'skills:changed',
  },
  statuses: {
    LIST: 'statuses:list',
    REORDER: 'statuses:reorder',
    CHANGED: 'statuses:changed',
  },
  labels: {
    LIST: 'labels:list',
    CREATE: 'labels:create',
    DELETE: 'labels:delete',
    CHANGED: 'labels:changed',
  },
  views: {
    LIST: 'views:list',
    SAVE: 'views:save',
  },
  toolIcons: {
    GET_MAPPINGS: 'toolIcons:getMappings',
  },
  logo: {
    GET_URL: 'logo:getUrl',
  },
  notification: {
    SHOW: 'notification:show',
    NAVIGATE: 'notification:navigate',
    GET_ENABLED: 'notification:getEnabled',
    SET_ENABLED: 'notification:setEnabled',
  },
  input: {
    GET_AUTO_CAPITALISATION: 'input:getAutoCapitalisation',
    SET_AUTO_CAPITALISATION: 'input:setAutoCapitalisation',
    GET_SEND_MESSAGE_KEY: 'input:getSendMessageKey',
    SET_SEND_MESSAGE_KEY: 'input:setSendMessageKey',
    GET_SPELL_CHECK: 'input:getSpellCheck',
    SET_SPELL_CHECK: 'input:setSpellCheck',
  },
  power: {
    GET_KEEP_AWAKE: 'power:getKeepAwake',
    SET_KEEP_AWAKE: 'power:setKeepAwake',
  },
  appearance: {
    GET_RICH_TOOL_DESCRIPTIONS: 'appearance:getRichToolDescriptions',
    SET_RICH_TOOL_DESCRIPTIONS: 'appearance:setRichToolDescriptions',
  },
  tools: {
    GET_BROWSER_TOOL_ENABLED: 'tools:getBrowserToolEnabled',
    SET_BROWSER_TOOL_ENABLED: 'tools:setBrowserToolEnabled',
    GET_NANO_BANANA_ENABLED: 'tools:getNanoBananaEnabled',
    SET_NANO_BANANA_ENABLED: 'tools:setNanoBananaEnabled',
    GET_IMAGE_GENERATION_PROVIDER: 'tools:getImageGenerationProvider',
    SET_IMAGE_GENERATION_PROVIDER: 'tools:setImageGenerationProvider',
    GET_IMAGE_UNDERSTANDING_ENABLED: 'tools:getImageUnderstandingEnabled',
    SET_IMAGE_UNDERSTANDING_ENABLED: 'tools:setImageUnderstandingEnabled',
    GET_BUILT_IN_IMAGE_TOOL_ROUTES: 'tools:getBuiltInImageToolRoutes',
    SET_BUILT_IN_IMAGE_TOOL_ROUTES: 'tools:setBuiltInImageToolRoutes',
  },
  caching: {
    GET_EXTENDED_PROMPT_CACHE: 'caching:getExtendedPromptCache',
    SET_EXTENDED_PROMPT_CACHE: 'caching:setExtendedPromptCache',
    GET_ENABLE_1M_CONTEXT: 'caching:getEnable1MContext',
    SET_ENABLE_1M_CONTEXT: 'caching:setEnable1MContext',
  },
  badge: {
    REFRESH: 'badge:refresh',
    SET_ICON: 'badge:setIcon',
    DRAW: 'badge:draw',
    DRAW_WINDOWS: 'badge:draw-windows',
  },
  releaseNotes: {
    GET: 'releaseNotes:get',
    GET_LATEST_VERSION: 'releaseNotes:getLatestVersion',
  },
  git: {
    GET_BRANCH: 'git:getBranch',
  },
  gitbash: {
    CHECK: 'gitbash:check',
    BROWSE: 'gitbash:browse',
    SET_PATH: 'gitbash:setPath',
    INSTALL: 'gitbash:install',
  },
  browserPane: {
    CREATE: 'browser-pane:create',
    DESTROY: 'browser-pane:destroy',
    LIST: 'browser-pane:list',
    NAVIGATE: 'browser-pane:navigate',
    GO_BACK: 'browser-pane:go-back',
    GO_FORWARD: 'browser-pane:go-forward',
    RELOAD: 'browser-pane:reload',
    STOP: 'browser-pane:stop',
    FOCUS: 'browser-pane:focus',
    SNAPSHOT: 'browser-pane:snapshot',
    CLICK: 'browser-pane:click',
    FILL: 'browser-pane:fill',
    SELECT: 'browser-pane:select',
    SCREENSHOT: 'browser-pane:screenshot',
    EVALUATE: 'browser-pane:evaluate',
    SCROLL: 'browser-pane:scroll',
    LAUNCH: 'browser-empty-state:launch',
    STATE_CHANGED: 'browser-pane:state-changed',
    REMOVED: 'browser-pane:removed',
    INTERACTED: 'browser-pane:interacted',
  },
  automations: {
    GET_CONFIG: 'automations:getConfig',
    UPSERT: 'automations:upsert',
    TEST: 'automations:test',
    SET_ENABLED: 'automations:setEnabled',
    DUPLICATE: 'automations:duplicate',
    DELETE: 'automations:delete',
    GET_HISTORY: 'automations:getHistory',
    GET_LAST_EXECUTED: 'automations:getLastExecuted',
    REPLAY: 'automations:replay',
    CHANGED: 'automations:changed',
  },
  okclawAuth: {
    GET_STATE: 'okclawAuth:getState',
    LOGIN_PASSWORD: 'okclawAuth:loginPassword',
    LOGIN_SMS: 'okclawAuth:loginSms',
    LOGIN_WECHAT: 'okclawAuth:loginWechat',
    SEND_SMS: 'okclawAuth:sendSms',
    GET_CAPTCHA: 'okclawAuth:getCaptcha',
    SEND_REGISTRY_SMS: 'okclawAuth:sendRegistrySms',
    REGISTER: 'okclawAuth:register',
    RESET_PASSWORD: 'okclawAuth:resetPassword',
    WECHAT_QR: 'okclawAuth:wechatQr',
    WECHAT_STATUS: 'okclawAuth:wechatStatus',
    WECHAT_BIND_QR: 'okclawAuth:wechatBindQr',
    WECHAT_BIND_STATUS: 'okclawAuth:wechatBindStatus',
    CURRENT_USER_INFO: 'okclawAuth:currentUserInfo',
    CURRENT_USER_BALANCE: 'okclawAuth:currentUserBalance',
    STUDENT_CERTIFICATION_INFO: 'okclawAuth:studentCertificationInfo',
    UPDATE_USER_NAME: 'okclawAuth:updateUserName',
    SEND_USER_PHONE_VALIDATE: 'okclawAuth:sendUserPhoneValidate',
    SEND_NEW_PHONE_VALIDATE: 'okclawAuth:sendNewPhoneValidate',
    UPDATE_PHONE: 'okclawAuth:updatePhone',
    SEND_USER_EMAIL_VALIDATE: 'okclawAuth:sendUserEmailValidate',
    SEND_NEW_EMAIL_VALIDATE: 'okclawAuth:sendNewEmailValidate',
    UPDATE_EMAIL: 'okclawAuth:updateEmail',
    SET_EMAIL: 'okclawAuth:setEmail',
    UPDATE_PASSWORD: 'okclawAuth:updatePassword',
    SET_PASSWORD: 'okclawAuth:setPassword',
    UPLOAD_OSS_FILE: 'okclawAuth:uploadOssFile',
    SUBMIT_STUDENT_CERTIFICATION: 'okclawAuth:submitStudentCertification',
    CREATE_RECHARGE_ORDER: 'okclawAuth:createRechargeOrder',
    REFRESH_TOKEN: 'okclawAuth:refreshToken',
    LOGOUT: 'okclawAuth:logout',
    REJECT_NEW_SESSION: 'okclawAuth:rejectNewSession',
    STATE_CHANGED: 'okclawAuth:stateChanged',
    SESSION_REVOKED: 'okclawAuth:sessionRevoked',
  },
  okclawSubscription: {
    GET_BALANCE: 'okclawSubscription:getBalance',
    LIST: 'okclawSubscription:list',
    KEY_LIST: 'okclawSubscription:keyList',
    AUTO_KEY: 'okclawSubscription:autoKey',
    PLAN_LIST: 'okclawSubscription:planList',
    TRAFFIC_PACKAGE_LIST: 'okclawSubscription:trafficPackageList',
    CREATE_BUY_ORDER: 'okclawSubscription:createBuyOrder',
    CREATE_RENEW_ORDER: 'okclawSubscription:createRenewOrder',
    CREATE_TRAFFIC_ORDER: 'okclawSubscription:createTrafficOrder',
    ORDER_DETAIL: 'okclawSubscription:orderDetail',
    TRANSACTION_STATE: 'okclawSubscription:transactionState',
    WX_PRE_PAY: 'okclawSubscription:wxPrePay',
    ALIPAY_PRE_PAY: 'okclawSubscription:alipayPrePay',
    BALANCE_PAY: 'okclawSubscription:balancePay',
    BALANCE_WX_PRE_PAY: 'okclawSubscription:balanceWxPrePay',
    BALANCE_ALIPAY_PRE_PAY: 'okclawSubscription:balanceAlipayPrePay',
    COUPON_LIST: 'okclawSubscription:couponList',
    AVAILABLE_DISCOUNT_OPTIONS: 'okclawSubscription:availableDiscountOptions',
    CALCULATE_DISCOUNT: 'okclawSubscription:calculateDiscount',
    AUTO_SET_KEY_AFTER_PURCHASE: 'okclawSubscription:autoSetKeyAfterPurchase',
    USE_SUBSCRIPTION: 'okclawSubscription:useSubscription',
    KEY_CONFIG_GET: 'okclawSubscription:keyConfigGet',
    KEY_CONFIG_SAVE: 'okclawSubscription:keyConfigSave',
    KEY_CONFIG_UPDATED: 'okclawSubscription:keyConfigUpdated',
    GET_INVITE_INFO: 'okclawSubscription:getInviteInfo',
  },
  mail: {
    GET_CONFIG: 'mail:get-config',
    SAVE_CONFIG: 'mail:save-config',
    TEST_CONFIG: 'mail:test-config',
  },
  feishu: {
    GET_CONFIG: 'feishu:get-config',
    SAVE_CONFIG: 'feishu:save-config',
    GET_DECRYPTED_SECRET: 'feishu:get-decrypted-secret',
    TEST_CONNECTION: 'feishu:test-connection',
    START_BRIDGE: 'feishu:start-bridge',
    STOP_BRIDGE: 'feishu:stop-bridge',
    GET_STATUS: 'feishu:get-status',
    STATUS_CHANGED: 'feishu:status-changed',
    LIST_BINDINGS: 'feishu:list-bindings',
    UPDATE_BINDING: 'feishu:update-binding',
    REMOVE_BINDING: 'feishu:remove-binding',
    DELETE_MESSAGE: 'feishu:delete-message',
    PIN_MESSAGE: 'feishu:pin-message',
    EDIT_MESSAGE: 'feishu:edit-message',
    FORWARD_MESSAGE: 'feishu:forward-message',
    URGENT_MESSAGE: 'feishu:urgent-message',
    GET_USER_INFO: 'feishu:get-user-info',
    LIST_BINDINGS_DISPLAY: 'feishu:list-bindings-display',
    REGISTER_APP_START: 'feishu:register-app-start',
    REGISTER_APP_CANCEL: 'feishu:register-app-cancel',
    REGISTER_APP_QRCODE: 'feishu:register-app-qrcode',
    REGISTER_APP_STATUS: 'feishu:register-app-status',
  },
  wecom: {
    GET_CONFIG: 'wecom:get-config',
    SAVE_CONFIG: 'wecom:save-config',
    GET_STATUS: 'wecom:get-status',
    STATUS_CHANGED: 'wecom:status-changed',
    START_BRIDGE: 'wecom:start-bridge',
    STOP_BRIDGE: 'wecom:stop-bridge',
    TEST_CONNECTION: 'wecom:test-connection',
    REQUEST_QR: 'wecom:request-qr',
    POLL_QR: 'wecom:poll-qr',
    LIST_BINDINGS: 'wecom:list-bindings',
    UPDATE_BINDING: 'wecom:update-binding',
    REMOVE_BINDING: 'wecom:remove-binding',
  },
  dingtalk: {
    GET_CONFIG: 'dingtalk:get-config',
    SAVE_CONFIG: 'dingtalk:save-config',
    GET_DECRYPTED_SECRET: 'dingtalk:get-decrypted-secret',
    TEST_CONNECTION: 'dingtalk:test-connection',
    START_BRIDGE: 'dingtalk:start-bridge',
    STOP_BRIDGE: 'dingtalk:stop-bridge',
    GET_STATUS: 'dingtalk:get-status',
    STATUS_CHANGED: 'dingtalk:status-changed',
    LIST_BINDINGS: 'dingtalk:list-bindings',
    UPDATE_BINDING: 'dingtalk:update-binding',
    REMOVE_BINDING: 'dingtalk:remove-binding',
    GET_USER_INFO: 'dingtalk:get-user-info',
    SEND_IMAGE: 'dingtalk:send-image',
    SEND_ACTION_CARD: 'dingtalk:send-action-card',
    UPLOAD_FILE: 'dingtalk:upload-file',
    SEND_FILE: 'dingtalk:send-file',
  },
  qqbot: {
    GET_CONFIG: 'qqbot:get-config',
    SAVE_CONFIG: 'qqbot:save-config',
    GET_DECRYPTED_SECRET: 'qqbot:get-decrypted-secret',
    TEST_CONNECTION: 'qqbot:test-connection',
    START_BRIDGE: 'qqbot:start-bridge',
    STOP_BRIDGE: 'qqbot:stop-bridge',
    GET_STATUS: 'qqbot:get-status',
    STATUS_CHANGED: 'qqbot:status-changed',
    LIST_BINDINGS: 'qqbot:list-bindings',
    UPDATE_BINDING: 'qqbot:update-binding',
    REMOVE_BINDING: 'qqbot:remove-binding',
  },
  weixinV2: {
    GET_CONFIG: 'weixin-v2:get-config',
    SAVE_CONFIG: 'weixin-v2:save-config',
    GET_STATUS: 'weixin-v2:get-status',
    STATUS_CHANGED: 'weixin-v2:status-changed',
    LIST_ACCOUNTS: 'weixin-v2:list-accounts',
    ACCOUNTS_CHANGED: 'weixin-v2:accounts-changed',
    UPDATE_ACCOUNT_REMARK: 'weixin-v2:update-account-remark',
    START_LOGIN: 'weixin-v2:start-login',
    AWAIT_LOGIN: 'weixin-v2:await-login',
    REMOVE_ACCOUNT: 'weixin-v2:remove-account',
    LIST_BINDINGS: 'weixin-v2:list-bindings',
    UPDATE_BINDING: 'weixin-v2:update-binding',
    REMOVE_BINDING: 'weixin-v2:remove-binding',
    NOTIFICATION_SENT: 'weixin-v2:notification-sent',
    DELIVERY_ERROR: 'weixin-v2:delivery-error',
  },
} as const

// IPC_CHANNELS compat alias removed — all consumers now use RPC_CHANNELS

/**
 * Flatten all channel string values from the nested RPC_CHANNELS object.
 * Used by the exhaustive routing test to ensure every channel is classified.
 */
export function getAllChannelValues(): string[] {
  const values: string[] = []
  for (const namespace of Object.values(RPC_CHANNELS)) {
    for (const channel of Object.values(namespace)) {
      values.push(channel)
    }
  }
  return values
}
